import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from '@chakra-ui/react';
import { IconButton, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';

import { useIsMobile } from '@izimi/hooks';
import type { DocumentEntity } from '@izimi/modules';

import { CustomIcon, FeatherIcon } from '../../../icon';
import { useModalStore } from '../../../modals';
import { useDocumentTrashOptions } from '../useDocumentTrashOptions';

interface Props extends Omit<MenuProps, 'children'> {
  document: DocumentEntity;
  children?: ReactNode;
}

const TrashDocumentsTableOptionsMenu = ({ document, children, ...menuProps }: Props) => {
  const { t } = useTranslation('shared');
  const { showModal } = useModalStore();
  const isMobile = useIsMobile();
  const { handleRestoreDocument, handlePermanentlyDeleteDocument } = useDocumentTrashOptions();

  if (isMobile) {
    return (
      <IconButton
        title={t('action.viewOptions')}
        onClick={() => showModal({ type: 'trashDocumentsTableOptions', document })}
        icon={<FeatherIcon name='more-horizontal' />}
        aria-label={t('action.viewOptions')}
      />
    );
  }

  return (
    <Menu
      isLazy
      placement='bottom-start'
      offset={[-20, 0]}
      variant='documentTableMenu'
      {...menuProps}>
      {children ?? (
        <MenuButton
          as={IconButton}
          aria-label={t('action.viewOptions')}
          title={t('action.viewOptions')}
          size='md'
          variant='action'
          icon={<FeatherIcon name='more-horizontal' />}
        />
      )}
      <MenuList>
        <MenuItem
          icon={<CustomIcon name='restore' />}
          onClick={() => handleRestoreDocument(document)}>
          {t('trash:restore')}
        </MenuItem>
        <MenuItem
          icon={<CustomIcon name='trash' color='secondary200' secondaryColor='secondary400' />}
          color='secondary400'
          onClick={() => handlePermanentlyDeleteDocument(document)}>
          {t('trash:permanentlyDelete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default TrashDocumentsTableOptionsMenu;
